/* =========================================================================
   Brother Theo — "the Little Monk". His CharacterDef + all v1.1.0 copy. Warm,
   reverent, gently encouraging; never preachy. Lines are bundled here (v1);
   they migrate to the app.CharacterLines CMS in 1.2 through the registry seam
   with zero code change. Self-registers on import.

   Registry moments shipped with lines (consumed by this + later waves):
     greeting-morning/afternoon/evening · correct · streak · hint · daily ·
     dailyLit · rankup · guestNudge · cameo · stretch-win · stretch-miss ·
     hard-encourage · dyk · gtky · journey-next ·
     quest-complete · feast-intro · feast-done · gallery-generic ·
     gallery:{votive_candles,marian_statue,stained_glass,chapel_altar,prayer_chapel} ·
     wheel-item · p1-intro · p1-step · generic
   A couple of memory-conditioned lines demonstrate the GTKY callback mechanism.
   ========================================================================= */
import type { CharacterDef } from './types';
import { registerCharacter } from './registry';

export const THEO: CharacterDef = {
  id: 'theo',
  name: 'Brother Theo',
  epithet: 'the Little Monk',
  emoji: '🧑‍🦲',
  art: {
    greet: 'media/characters/theo-greet.png',
    welcome: 'media/characters/theo-welcome.png',
    teach: 'media/characters/theo-teach.png',
    pray: 'media/characters/theo-pray.png',
  },
  poseFor: {
    'greeting-morning': 'greet',
    'greeting-afternoon': 'greet',
    'greeting-evening': 'greet',
    correct: 'teach',
    dyk: 'teach',
    hint: 'teach',
    daily: 'welcome',
    dailyLit: 'welcome',
    rankup: 'welcome',
    streak: 'pray',
    cameo: 'pray',
    'stretch-win': 'teach',
    'stretch-miss': 'teach',
    'hard-encourage': 'pray',
    guestNudge: 'welcome',
  },
  lines: {
    'greeting-morning': [
      { text: 'Good morning, friend. A new day, a new page of the faith to turn together.' },
      { text: "Morning bells. Let's begin. I've kept a candle lit for you." },
      { text: 'Rise and shine in grace. Shall we learn something beautiful today?' },
      { text: 'Good morning, {name}. A new day, a new page of the faith to turn together.' },
    ],
    'greeting-afternoon': [
      { text: 'Good afternoon. The day is bright, a fine hour for a little wisdom.' },
      { text: 'Peace be with you this afternoon. Ready when you are.' },
      { text: 'Welcome back. Even a few questions can water the soul.' },
      { text: 'Good afternoon, {name}. A fine hour for a little wisdom.' },
    ],
    'greeting-evening': [
      { text: 'Good evening, friend. A quiet hour is a good hour to learn.' },
      { text: 'Evening peace to you. Let us end the day a little wiser.' },
      { text: 'The lamps are lit and the cloister is calm. Come, sit awhile.' },
      { text: 'Good evening, {name}. A quiet hour is a good hour to learn.' },
      // GTKY payback (§6): the evening is the Rosary's hour, so the player's own
      // Mystery comes back to them here. Key: favorite_mystery (T3).
      {
        text: 'Evening is the Rosary\'s hour. Perhaps the Mysteries you love best are waiting for you tonight.',
        requiresMemoryKey: 'favorite_mystery',
      },
      {
        text: 'Good evening, {name}. I thought of the Mysteries you told me of. They keep good company at this hour.',
        requiresMemoryKey: 'favorite_mystery',
      },
    ],
    correct: [
      { text: 'Well done. You have a gift for this.' },
      { text: 'Beautifully answered. Deo gratias.' },
      { text: "That's the way. Your bank grows and so does your wisdom." },
      { text: 'Good. Keep that light burning.' },
    ],
    streak: [
      { text: 'Day after day you return. Faithfulness is its own reward.' },
      { text: 'Your streak burns bright. What a steady flame you keep.' },
      { text: 'Another day in a row. The small, faithful steps are the holy ones.' },
    ],
    hint: [
      { text: "There. I've quietly set two aside. Now the path is clearer." },
      { text: 'Two options rest; two remain. Choose with confidence, friend.' },
      { text: 'A little help from the cloister. The clock still runs, so be swift.' },
    ],
    daily: [
      { text: 'Play a round today and I will light our Daily Blessing candle.' },
      { text: 'The candle waits, unlit. One game today, and we kindle it together.' },
      // GTKY payback (§6). Key: favorite_devotion (T3) — deliberately unvalued,
      // so it reads warmly whichever devotion the player named.
      {
        text: 'One round today and our candle is lit. A small thing to set beside the devotion you hold dear.',
        requiresMemoryKey: 'favorite_devotion',
      },
      {
        text: 'The candle waits. You told me what you love to pray. This is a good day to do both.',
        requiresMemoryKey: 'favorite_devotion',
      },
    ],
    dailyLit: [
      { text: "Today's blessing is lit. The little flame is yours. Come back tomorrow." },
      { text: 'Our candle glows for today. Well kept, faithful friend.' },
    ],
    rankup: [
      { text: 'You have grown, and so my lamp burns brighter. Onward, friend.' },
      { text: 'A new rank. See how the light rises. Your diligence shines through me.' },
      { text: 'Higher still. Every answer has trimmed my wick a little brighter.' },
      // GTKY payback (§6). Key: confirmation_saint (T3) — free-text, so the line
      // gestures at the saint rather than quoting the answer back verbatim.
      {
        text: 'A new rank. I rather think the saint you took at Confirmation is pleased with you today.',
        requiresMemoryKey: 'confirmation_saint',
      },
      {
        text: 'You have grown again, and your Confirmation patron has been walking with you the whole way.',
        requiresMemoryKey: 'confirmation_saint',
      },
    ],
    guestNudge: [
      { text: 'Make a free account and I will remember your streak, your bank, and your growth.' },
      { text: "Sign in and nothing you earn is lost. I'll keep the light burning between visits." },
    ],
    cameo: [
      { text: 'The final round. Steady your heart. You were made for this.' },
      { text: 'One last round, friend. Whatever the score, you have learned much today.' },
      { text: 'Here we are at the end of the road. Finish strong, and finish joyful.' },
      {
        text: 'May your favourite saint pray for you in this final round.',
        requiresMemoryKey: 'favorite_saint',
      },
    ],
    /* Friendly Play pop-ins (Eric 2026-08-16): ambient, warm, never about
       scores or money — study mode has no clock and no coins, and Theo's job
       here is to make that feel like a feature. Scheduling lives in
       friendlyTheo.ts (capped per session); these are only the words. */
    'friendly-welcome': [
      { text: 'No clock in the cloister. We read, we wonder, we learn.' },
      { text: 'Study hall is open. Ask me nothing; I shall beam at you regardless.' },
      { text: 'Ah, a scholar returns to the library. Sit, sit. The questions can wait for you.' },
      { text: 'Welcome, {name}. Here we go slowly, and slowly is how roots grow deep.' },
    ],
    'friendly-ponder': [
      { text: 'Take your time, friend. No penalty for pondering.' },
      { text: 'Sit with it awhile. The monks would approve.' },
      { text: 'A slow answer that teaches beats a quick one that fades.' },
      { text: 'Thinking is praying with the lamp on, some say. Take your time.' },
    ],
    'friendly-miss': [
      { text: 'Even Aquinas crossed things out. Read the source and it is yours forever.' },
      { text: 'A miss in study hall costs nothing. That is why we are here.' },
      { text: 'Good. Now you know the shape of the gap. The next read fills it.' },
      { text: 'The wrong turn that teaches the road is not wasted, friend.' },
    ],
    'friendly-streak': [
      { text: 'Three in a row. The lamp burns steadier already.' },
      { text: 'Look at you. The pages are starting to stick.' },
      { text: 'A fine little run. Quiet study, loud results.' },
    ],
    'friendly-source': [
      { text: 'Do read the citation lines. The source is half the treasure.' },
      { text: 'Under each answer sits its source. That little line is where the learning lives.' },
      { text: 'The answers are good; the citations are better. Linger on them.' },
    ],
    'friendly-finish': [
      { text: 'A set well studied. What you met today will greet you again like a friend.' },
      { text: 'Done. Not every seed sprouts the same day it is sown. Well studied.' },
      { text: 'The cloister is proud of you. Come back for another set anytime.' },
    ],
    /* Hard-mode on-ramp (PLAN-gameplay-v2 §3b). The ⭐ stretch question is a
       free taste of the deep end — Theo's job is to make the reach itself feel
       worthwhile, win or miss, and to meet a lost hard round with warmth
       instead of silence. Scheduling lives in gameController. */
    'stretch-win': [
      { text: 'A stretch question, and you reached it. That is seminary air you are breathing.' },
      { text: 'Look at that. The deep end, and you swam. Deo gratias.' },
      { text: 'Doctor-of-the-Church territory, and you held your own. Wonderful.' },
      // GTKY payback (§6). Key: wants_harder (T2), value-matched — only the
      // player who ASKED for the deep end is told the deep end is coming.
      {
        text: 'You asked me for harder questions, and there is your proof you can carry them. More to come.',
        requiresMemoryKey: 'wants_harder',
        requiresMemoryValue: 'Yes — challenge me',
      },
      {
        text: 'A stretch question, cleanly taken. You wanted the challenge, and the challenge suits you.',
        requiresMemoryKey: 'wants_harder',
        requiresMemoryValue: 'Yes — challenge me',
      },
    ],
    'stretch-miss': [
      { text: 'That one was above the line on purpose. No cost, only a look at the deeper water.' },
      { text: 'A stretch is meant to be a stretch. Nothing lost, and now you have met it once.' },
      { text: 'Even the great doctors were beginners here. You have seen it now, and that is the whole point.' },
    ],
    'hard-encourage': [
      { text: 'Hard mode is meant to be hard, friend. You went looking for the deep end. That is courage, not failure.' },
      { text: 'A rough round on the hard road. Every one of these questions teaches twice.' },
      { text: 'Steady. The scholars you are reading spent decades on this. A round is a fine start.' },
    ],
    dyk: [
      { text: 'Ah, a small treasure of the faith. Let me show you.' },
      { text: 'Pause a moment; here is something worth knowing.' },
      { text: 'The cloister loves a good fact. Listen to this one.' },
    ],
    gtky: [
      { text: 'May I ask you something? I love to know the friends I walk with.' },
      { text: "Tell me a little about yourself. It helps me walk beside you." },
      { text: 'A gentle question, if you will. No wrong answers here.' },
    ],
    'journey-next': [
      { text: "Well done. Now, shall we visit the next stop on today's little pilgrimage?" },
      { text: "There's one more good thing to do today. Let me point the way." },
      { text: 'The road continues. Follow me to the next stop when you are ready.' },
      { text: "Well done, {name}. Shall we visit the next stop on today's little pilgrimage?" },
    ],
    'quest-complete': [
      { text: 'A task fulfilled. Small faithful deeds, gathered like beads on a rosary.' },
      { text: "Done, and done well. That's one more good work in today's basket." },
      { text: 'You finished it. Heaven notices the little things too.' },
    ],
    'feast-intro': [
      { text: "Today the Church keeps a feast. Come, let me tell you the story." },
      { text: 'Every day has its saint or mystery. Here is today\'s. Gather round.' },
      {
        text: "You've walked Rome's own streets. Today the whole Church keeps this feast with you.",
        requiresMemoryKey: 'visited_rome',
        requiresMemoryValue: 'Yes',
      },
    ],
    'feast-done': [
      { text: "A blessed feast to you. Keep this one in your heart today." },
      { text: "There, a badge for today's feast. May the saints walk with you." },
    ],
    'gallery-generic': [
      { text: 'Each piece you bring restores our little chapel. Heaven meets earth in beauty.' },
      { text: 'A worthy addition. Beauty in worship lifts the whole soul.' },
    ],
    'gallery:votive_candles': [
      { text: 'Votive candles. Each a silent prayer that keeps burning after you have gone.' },
    ],
    'gallery:marian_statue': [
      { text: 'Our Lady watches over the chapel now. To Jesus, always through Mary.' },
    ],
    'gallery:stained_glass': [
      { text: 'Stained glass. A sermon in light, teaching the faith to all who look up.' },
    ],
    'gallery:chapel_altar': [
      { text: 'The altar is where heaven meets earth. You have given our chapel its very heart.' },
    ],
    'gallery:prayer_chapel': [
      { text: 'A whole prayer chapel. What began in ruins is radiant now, thanks to you.' },
    ],
    'wheel-item': [
      { text: 'Providence smiles. A little treasure for the road. Keep it well.' },
      { text: 'Look what the wheel has given you. Every gift has its purpose.' },
    ],
    'p1-intro': [
      { text: '"Learning the Faith" is our first pilgrimage. Ten steps, one at a time, together.' },
      { text: 'Every pilgrimage begins with a single step. Let me walk this road with you.' },
      { text: 'Welcome, {name}, to the first steps of our pilgrimage. Ten steps, one at a time, together.' },
    ],
    'p1-step': [
      { text: 'One step nearer. See how the path lights up behind you?' },
      { text: 'Another marker passed. The hilltop chapel draws closer.' },
    ],
    generic: [{ text: 'Peace be with you, friend.' }],
  },
};

registerCharacter(THEO);
